/**
 * HeadingCollapse - Notion-style collapsible headings. Every heading that has a body (content
 * up to the next heading of the same or higher level) gets a chevron toggle; clicking it hides
 * that range. Purely presentational: collapse state lives in the plugin (never in the document),
 * so it changes nothing that gets saved/harvested and works the same in read-only view and in the
 * editor. Hiding is a `display:none` node decoration, so collapsing an H1 also hides the H2/H3
 * groups nested under it - exactly the section/group behavior Notion gives.
 *
 * State survives typing: RichTextEditor only re-runs setContent when the incoming doc differs from
 * the editor's own JSON, and a collapse toggle is a meta-only transaction (no doc change), so it
 * neither dirties the draft nor resets on keystrokes. Collapse keys are the heading's model
 * `dataKey` (see headingKeys.ts) when present, else its level+text - stable enough for ephemeral UI
 * state; a key that no longer matches any heading simply hides nothing.
 */
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
const collapseKey = new PluginKey('headingCollapse');
/** Right-pointing triangle; CSS rotates it to point down when the section is expanded. */
const CHEVRON_SVG = '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">' +
    '<path fill="currentColor" d="M3 1.5 L7.5 5 L3 8.5 Z"/></svg>';
function headingLevel(node) {
    return Number(node.attrs.level ?? 1);
}
/**
 * Stable-ish collapse key: the model dataKey, else level+text. Null when neither is usable.
 * Exported so hosts (e.g. the Scope doc's outline nav) can address the SAME keys this plugin
 * collapses by.
 */
export function headingCollapseKey(node) {
    if (node.type.name !== 'heading')
        return null;
    const raw = node.attrs.dataKey;
    if (typeof raw === 'string' && raw)
        return raw;
    const txt = node.textContent.trim();
    return txt ? `h${headingLevel(node)}:${txt}` : null;
}
/** The doc's direct children with their absolute start/end positions (the scope doc is flat). */
function topChildren(doc) {
    const out = [];
    doc.forEach((node, offset) => {
        out.push({ node, start: offset, end: offset + node.nodeSize });
    });
    return out;
}
/** Index of the first child after `i` that ends heading `i`'s body (next heading of level <= it). */
function sectionEndIdx(children, i, level) {
    for (let j = i + 1; j < children.length; j++) {
        const n = children[j].node;
        if (n.type.name === 'heading' && headingLevel(n) <= level)
            return j;
    }
    return children.length;
}
/** Every heading key that has a body - the exact set the chevrons render for (collapse-all target). */
function collapsibleKeys(doc) {
    const children = topChildren(doc);
    const keys = new Set();
    for (let i = 0; i < children.length; i++) {
        const { node } = children[i];
        if (node.type.name !== 'heading')
            continue;
        const key = headingCollapseKey(node);
        if (!key)
            continue;
        if (sectionEndIdx(children, i, headingLevel(node)) > i + 1)
            keys.add(key);
    }
    return keys;
}
function makeChevron(view, key, isCollapsed) {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'rte-collapse-toggle' + (isCollapsed ? ' rte-collapse-toggle--collapsed' : '');
    el.setAttribute('contenteditable', 'false');
    el.setAttribute('aria-label', isCollapsed ? 'Expand section' : 'Collapse section');
    el.setAttribute('aria-expanded', String(!isCollapsed));
    el.innerHTML = CHEVRON_SVG;
    // Do not let the click start a text selection / drag; just toggle collapse state.
    el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
    });
    el.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        view.dispatch(view.state.tr.setMeta(collapseKey, { toggle: key }));
    });
    return el;
}
function buildDecorations(state, collapsed) {
    const children = topChildren(state.doc);
    const decos = [];
    for (let i = 0; i < children.length; i++) {
        const { node, start, end } = children[i];
        if (node.type.name !== 'heading')
            continue;
        const key = headingCollapseKey(node);
        if (!key)
            continue;
        const level = headingLevel(node);
        // Section body = every following top-level child up to the next heading of level <= this one.
        const endIdx = sectionEndIdx(children, i, level);
        if (endIdx <= i + 1)
            continue; // no body -> nothing to collapse, no chevron
        const isCollapsed = collapsed.has(key);
        // Chevron at the start of the heading's content (side -1), keyed by state so it re-renders on flip.
        decos.push(Decoration.widget(start + 1, (view) => makeChevron(view, key, isCollapsed), {
            side: -1,
            key: `hc-${key}-${isCollapsed ? 'c' : 'e'}`,
        }));
        decos.push(Decoration.node(start, end, {
            class: isCollapsed ? 'rte-heading--collapsed' : 'rte-heading--collapsible',
        }));
        if (isCollapsed) {
            for (let j = i + 1; j < endIdx; j++) {
                const child = children[j];
                decos.push(Decoration.node(child.start, child.end, { class: 'rte-collapsed-hidden' }));
            }
        }
    }
    return DecorationSet.create(state.doc, decos);
}
export const HeadingCollapse = Extension.create({
    name: 'headingCollapse',
    addOptions() {
        return { getEnabled: () => false };
    },
    addProseMirrorPlugins() {
        const getEnabled = () => this.options.getEnabled();
        return [
            new Plugin({
                key: collapseKey,
                state: {
                    init: () => ({ collapsed: new Set() }),
                    apply(tr, value) {
                        const meta = tr.getMeta(collapseKey);
                        if (meta?.collapseAll)
                            return { collapsed: collapsibleKeys(tr.doc) };
                        if (meta?.expandAll)
                            return { collapsed: new Set() };
                        if (meta?.expand?.length) {
                            const next = new Set(value.collapsed);
                            for (const k of meta.expand)
                                next.delete(k);
                            return { collapsed: next };
                        }
                        if (meta?.toggle) {
                            const next = new Set(value.collapsed);
                            if (next.has(meta.toggle))
                                next.delete(meta.toggle);
                            else
                                next.add(meta.toggle);
                            return { collapsed: next };
                        }
                        return value;
                    },
                },
                props: {
                    decorations(state) {
                        if (!getEnabled())
                            return DecorationSet.empty;
                        const self = collapseKey.getState(state);
                        return buildDecorations(state, self?.collapsed ?? new Set());
                    },
                },
            }),
        ];
    },
});
// ── Host helpers ──────────────────────────────────────────────────────────────
// Plain exported functions (not Tiptap commands) so apps without a direct @tiptap/core dependency
// can drive collapse state; each is a META-ONLY transaction - no `update` event, no draft dirty,
// no autosave arming (see the module docblock invariant). `collapseKey` stays module-private.
/** Collapse every heading that currently has a body (the exact chevron set). */
export function collapseAllHeadings(editor) {
    editor.view.dispatch(editor.state.tr.setMeta(collapseKey, { collapseAll: true }));
}
/** Expand everything. */
export function expandAllHeadings(editor) {
    editor.view.dispatch(editor.state.tr.setMeta(collapseKey, { expandAll: true }));
}
/** Expand just the given collapse keys (e.g. before scrolling to a heading inside a folded H1). */
export function expandCollapsedHeadings(editor, keys) {
    if (keys.length === 0)
        return;
    editor.view.dispatch(editor.state.tr.setMeta(collapseKey, { expand: keys }));
}
//# sourceMappingURL=headingCollapse.js.map